import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Typography
} from '@mui/material';
import { Poll, PollResponse, ResponseValue, User, Voter } from '../../types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where
} from 'firebase/firestore';

import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { useParams } from 'react-router-dom';

interface PollResponseAnswerCardProps {
  answer: ResponseValue;
}

const PollResponseAnswerCard: React.FC<PollResponseAnswerCardProps> = ({ answer }) => {
  const [voter, setVoter] = useState<Voter | null>(null);

  useEffect(() => {
    if (answer) {
      if (typeof answer === 'object' && 'name' in answer && 'id' in answer) {
        setVoter(answer as unknown as Voter);
      }
    }
  }, [answer]);

  return (
    <>
      {voter ? (
        <Typography component="span" variant="body2" color="textSecondary">
          <Box>
            <Typography><strong>Name:</strong> {voter.name}</Typography>
            <Typography><strong>Precinct:</strong> {voter.precinct}</Typography>
            <Typography><strong>Barangay:</strong> {voter.barangay}</Typography>
            <Typography><strong>City/Municipality:</strong> {voter.city_municipality}</Typography>
            <Typography><strong>Province:</strong> {voter.province}</Typography>
          </Box>
        </Typography>
      ) : (
        <Typography component="span" variant="body2" color="textSecondary">
          {answer?.toString()}
        </Typography>
      )}
    </>
  );
}

interface PollResponseCardProps {
  id: string;
  index: number;
  response: PollResponse;
  canManagePolls: boolean;
  onVoid: (id: string) => void;
  onUnvoid: (id: string) => void;
  onDelete: (id: string) => void;
}

const PollResponseCard: React.FC<PollResponseCardProps> = ({ id, index, response, canManagePolls, onVoid, onUnvoid, onDelete }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [questions, setQuestions] = useState<string[]>([]);

  useEffect(() => {
    const qs: string[] = [];
    Object.entries(response.responses).forEach(([question, answer]) => (
      qs.push(question)
    ));
    setQuestions(qs.sort((a, b) => a.localeCompare(b)));
  }, [response]);

  const handleVoidClick = () => {
    onVoid(response.id);
  }

  const handleUnvoidClick = () => {
    onUnvoid(response.id);
  }

  const handleDeleteClick = () => {
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    onDelete(response.id);
    setOpenDialog(false);
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            {index + 1} - {response.submittedAt.toDate().toLocaleString()}
          </Typography>
          <Box>
            {!response.voided && (
              <IconButton 
                onClick={handleVoidClick} 
                color="error" 
                disabled={!canManagePolls}
              >
                <BlockIcon />
              </IconButton>
            )}
            {response.voided && (
              <IconButton 
                onClick={handleUnvoidClick} 
                color="primary" 
                disabled={!canManagePolls}
              >
                <RestoreIcon />
              </IconButton>
            )}
            <IconButton 
              onClick={handleDeleteClick} 
              color="error" 
              disabled={!canManagePolls}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
        {questions.map(question => (
          <React.Fragment key={question}>
            <Typography component="span" variant="body1" color="textPrimary">
              {question.replace(/^"|"$/g, '')}: &nbsp;
            </Typography>
            <PollResponseAnswerCard answer={response.responses[question]} />
            <Box mb={1} />
          </React.Fragment>
        ))}
        <Typography variant="body2" color="textSecondary">
          Submitted By: {response.submittedBy}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          ID: {id}
        </Typography>
        {response.voided && (
          <Typography variant="body2" color="error">
            This response has been voided.
          </Typography>
        )}
      </CardContent>
      <Dialog
        open={openDialog}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this poll response? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

const PollResponses: React.FC = () => {
  const [polls, setPolls] = useState<Poll[]>([]);
  const [selectedPollId, setSelectedPollId] = useState<string>('');
  const [responses, setResponses] = useState<PollResponse[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [showVoided, setShowVoided] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const { user, loading: authLoading } = useAuth();

  // Prevent multiple simultaneous fetch requests
  const isFetchingRef = useRef(false);

  const RESPONSES_PER_PAGE = 20;

  // Fetch polls on component mount
  useEffect(() => {
    const fetchPolls = async (user: User) => {
      try {
        const pollsCollection = collection(db, 'polls');
        const pollsQuery = query(pollsCollection, where("archived", "==", false));
        const pollsSnapshot = await getDocs(pollsQuery);
        const pollsList = pollsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Poll[];
        pollsList.sort((a, b) => a.title.localeCompare(b.title));

        if (user.allowedPolls) {
          setPolls(pollsList.filter(poll => user.allowedPolls!.includes(poll.id)));
        } else {
          setPolls(pollsList);
        }
      } catch (error) {
        console.error("Error fetching polls:", error);
      }
    };

    if (user) {
      fetchPolls(user);
    }
  }, [user]);

  // Memoized fetch responses function to prevent unnecessary re-renders
  const fetchResponses = useCallback(async (reset = false) => {
    // Prevent multiple simultaneous fetch requests
    if (isFetchingRef.current || !selectedPollId) return;
    
    isFetchingRef.current = true;
    
    // Differentiate between initial load and pagination load
    if (reset) {
      setInitialLoading(true);
    } else {
      setPaginationLoading(true);
    }

    try {
      const responsesCollection = collection(db, 'pollResponses');

      let q = query(
        responsesCollection, 
        limit(RESPONSES_PER_PAGE),
        orderBy("submittedAt", "desc"),
        where("pollId", "==", selectedPollId)
      );

      if (!user?.permissions.view_responses) {
        q = query(q, where("submittedBy", "==", user?.display_name));
      }

      if (!reset && lastVisible) {
        q = query(q, startAfter(lastVisible));
      }

      const responsesSnapshot = await getDocs(q);
      const responsesList = responsesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PollResponse[];

      // Update responses based on reset or pagination
      setResponses(prev => reset ? responsesList : [...prev, ...responsesList]);
      
      // Update pagination state
      if (responsesList.length < RESPONSES_PER_PAGE) {
        setHasMore(false);
      } else {
        setLastVisible(responsesSnapshot.docs[responsesSnapshot.docs.length - 1]);
      }
    } catch (error) {
      console.error("Error fetching poll responses:", error);
      setHasMore(false);
    } finally {
      if (reset) {
        setInitialLoading(false);
      } else {
        setPaginationLoading(false);
      }
      isFetchingRef.current = false;
    }
  }, [selectedPollId]);

  // Reset responses when poll changes
  useEffect(() => {
    setResponses([]);
    setLastVisible(null);
    setHasMore(true);
    fetchResponses(true);
  }, [selectedPollId, fetchResponses]);

  // Infinite scroll intersection observer
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialLoading || !hasMore) return;

    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !initialLoading && !isFetchingRef.current) {
        fetchResponses();
      }
    };

    // Disconnect previous observer if exists
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '20px',
      threshold: 1.0
    });

    if (loadMoreTriggerRef.current) {
      observerRef.current.observe(loadMoreTriggerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [fetchResponses, initialLoading, hasMore]);

  // Rest of the component remains the same (handlePollChange, handleVoidResponse, etc.)
  const handlePollChange = (event: SelectChangeEvent<string>) => {
    setSelectedPollId(event.target.value);
  };

  const handleVoidResponse = async (id: string) => {
    try {
      await updateDoc(doc(db, 'pollResponses', id), { voided: true });
      setResponses(prevResponses => 
        prevResponses.map(response => 
          response.id === id ? { ...response, voided: true } : response
        )
      );
    } catch (error) {
      console.error("Error voiding poll response:", error);
    }
  };

  const handleUnvoidResponse = async (id: string) => {
    try {
      await updateDoc(doc(db, 'pollResponses', id), { voided: false });
      setResponses(prevResponses => 
        prevResponses.map(response => 
          response.id === id ? { ...response, voided: false } : response
        )
      );
    } catch (error) {
      console.error("Error unvoiding poll response:", error);
    }
  };

  const handleDeleteResponse = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'pollResponses', id));
      setResponses(prevResponses => prevResponses.filter(response => response.id !== id));
    } catch (error) {
      console.error("Error deleting poll response:", error);
    }
  };

  const handleShowVoidedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowVoided(event.target.checked);
  };

  const filteredResponses = useMemo(() => 
    showVoided ? responses : responses.filter(response => !response.voided), 
    [responses, showVoided]
  );

  if (authLoading) {
    return (
      <Paper elevation={3} style={{ padding: '20px', margin: '20px 0', textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
        <Typography variant="h4">
          Poll Responses
        </Typography>
        <Box display="flex" alignItems="center">
          <FormControl component="fieldset" sx={{ mr: 4 }}>
            <Box display="flex" alignItems="center">
              <Switch
                checked={showVoided}
                onChange={handleShowVoidedChange}
                name="showVoided"
                color="primary"
              />
              <Typography component="legend" style={{ marginLeft: 8 }}>Show Voided</Typography>
            </Box>
          </FormControl>
          <FormControl variant="outlined" style={{ minWidth: 200, marginRight: 16 }}>
            <InputLabel id="poll-select-label">Select Poll</InputLabel>
            <Select
              labelId="poll-select-label"
              id="poll-select"
              value={selectedPollId ?? ''}
              onChange={handlePollChange}
              label="Select Poll"
            >
              <MenuItem key={'select_poll'} value={''}>
                Select Poll
              </MenuItem>
              {polls.map((poll) => (
                <MenuItem key={poll.id} value={poll.id}>
                  {poll.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {selectedPollId === '' && <Typography>Please select a poll to view its responses.</Typography>}
      {filteredResponses.length === 0 && selectedPollId !== '' && <Typography>No responses available for this poll.</Typography>}
      {filteredResponses.length > 0 && (
        <>
          <List>
            {filteredResponses.map((response, index) => (
              <PollResponseCard 
                key={response.id} 
                id={response.id}
                index={index}
                response={response} 
                canManagePolls={user?.permissions?.manage_polls ?? false}
                onVoid={handleVoidResponse}
                onUnvoid={handleUnvoidResponse}
                onDelete={handleDeleteResponse} 
              />
            ))}
          </List>
          {hasMore && (
            <Box 
              ref={loadMoreTriggerRef} 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              padding={2}
            >
              {paginationLoading ? (
                <Box display="flex" alignItems="center">
                  <CircularProgress size={24} style={{ marginRight: 16 }} />
                  <Typography variant="body2" color="textSecondary">
                    Loading more responses...
                  </Typography>
                </Box>
              ) : null}
            </Box>
          )}
          {!hasMore && filteredResponses.length > 0 && (
            <Typography align="center" variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              No more responses to load
            </Typography>
          )}
        </>
      )}
    </>
  );
};

export default PollResponses;
